import { useEffect, useRef } from 'react';
import type { EventsListScrollTab } from '@/lib/types/events-scroll.types';
import {
  clearEventsListScrollSnapshot,
  getAppScrollContainer,
  getCurrentHistoryIndex,
  getEventCardSelector,
  isValidEventsListScrollSnapshot,
  readEventsListScrollSnapshot,
} from '@/lib/utils/events-scroll.utils';

const RESTORE_RETRY_WINDOW_MS = 600;
const RESTORE_TOLERANCE_PX = 2;

interface UseEventsListScrollRestorationProps {
  activeTab: EventsListScrollTab;
  isSearchMode: boolean;
  isLoadingActiveTab: boolean;
  activeLoadedItemCount: number;
  activeLoadedPageCount: number;
  activeHasNextPage?: boolean;
}

export function useEventsListScrollRestoration({
  activeTab,
  isSearchMode,
  isLoadingActiveTab,
  activeLoadedItemCount,
  activeLoadedPageCount,
  activeHasNextPage,
}: UseEventsListScrollRestorationProps) {
  const hasCompletedRef = useRef(false);
  const attemptInProgressRef = useRef(false);
  const latestMetaRef = useRef({
    isLoadingActiveTab,
    activeLoadedItemCount,
    activeLoadedPageCount,
    activeHasNextPage,
  });

  latestMetaRef.current = {
    isLoadingActiveTab,
    activeLoadedItemCount,
    activeLoadedPageCount,
    activeHasNextPage,
  };

  useEffect(() => {
    if (hasCompletedRef.current || isSearchMode) {
      return;
    }

    const snapshot = readEventsListScrollSnapshot();
    if (!snapshot) {
      return;
    }

    if (!isValidEventsListScrollSnapshot(snapshot)) {
      clearEventsListScrollSnapshot();
      return;
    }

    if (snapshot.mode !== 'tabs') {
      clearEventsListScrollSnapshot();
      return;
    }

    if (snapshot.tab !== activeTab) {
      clearEventsListScrollSnapshot();
      return;
    }

    if (
      typeof window !== 'undefined' &&
      snapshot.pathname !== window.location.pathname
    ) {
      clearEventsListScrollSnapshot();
      return;
    }

    const currentHistoryIndex = getCurrentHistoryIndex();
    if (
      snapshot.historyIndex !== null &&
      currentHistoryIndex !== null &&
      snapshot.historyIndex !== currentHistoryIndex
    ) {
      clearEventsListScrollSnapshot();
      return;
    }

    if (isLoadingActiveTab && activeLoadedItemCount === 0) {
      return;
    }

    const container = getAppScrollContainer();
    if (!container || attemptInProgressRef.current) {
      return;
    }

    attemptInProgressRef.current = true;
    let rafId: number | null = null;
    let cancelled = false;
    let userInteracted = false;

    const deadline = performance.now() + RESTORE_RETRY_WINDOW_MS;

    const cleanupListeners = () => {
      container.removeEventListener('wheel', markUserInteracted);
      container.removeEventListener('touchstart', markUserInteracted);
      container.removeEventListener('pointerdown', markUserInteracted);
    };

    const finish = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      cleanupListeners();
      attemptInProgressRef.current = false;
      hasCompletedRef.current = true;
      clearEventsListScrollSnapshot();
    };

    const markUserInteracted = () => {
      userInteracted = true;
    };

    container.addEventListener('wheel', markUserInteracted, { passive: true });
    container.addEventListener('touchstart', markUserInteracted, {
      passive: true,
    });
    container.addEventListener('pointerdown', markUserInteracted, {
      passive: true,
    });

    const tryRestore = () => {
      if (cancelled) {
        return;
      }

      if (userInteracted) {
        finish();
        return;
      }

      const currentContainer = getAppScrollContainer();
      if (!currentContainer) {
        finish();
        return;
      }

      currentContainer.scrollTop = snapshot.scrollTop;

      const exactRestoreReached =
        Math.abs(currentContainer.scrollTop - snapshot.scrollTop) <=
        RESTORE_TOLERANCE_PX;

      if (exactRestoreReached) {
        finish();
        return;
      }

      const maxScrollTop = Math.max(
        0,
        currentContainer.scrollHeight - currentContainer.clientHeight
      );
      const canReachExactTarget =
        maxScrollTop + RESTORE_TOLERANCE_PX >= snapshot.scrollTop;

      if (canReachExactTarget) {
        // One more attempt on the next frame handles late layout updates in the same tick.
        if (performance.now() < deadline) {
          rafId = requestAnimationFrame(tryRestore);
          return;
        }

        finish();
        return;
      }

      const latest = latestMetaRef.current;
      const likelyAtStableListSize =
        !latest.isLoadingActiveTab &&
        latest.activeLoadedPageCount >= snapshot.loadedPageCount &&
        latest.activeLoadedItemCount >= snapshot.loadedItemCount;

      if (likelyAtStableListSize || performance.now() >= deadline) {
        if (snapshot.clickedEventId) {
          const anchor = document.querySelector(
            getEventCardSelector(snapshot.clickedEventId)
          );
          if (anchor instanceof HTMLElement) {
            anchor.scrollIntoView({
              block: 'center',
              behavior: 'auto',
            });
          }
        }

        finish();
        return;
      }

      rafId = requestAnimationFrame(tryRestore);
    };

    rafId = requestAnimationFrame(tryRestore);

    return () => {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      cleanupListeners();
      attemptInProgressRef.current = false;
    };
  }, [
    activeHasNextPage,
    activeLoadedItemCount,
    activeLoadedPageCount,
    activeTab,
    isLoadingActiveTab,
    isSearchMode,
  ]);
}
